import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { EntityStatus } from "../types";
import {
  type HubPageSize,
  parseHubPageSize,
  hubPageSizeToParam,
  DEFAULT_HUB_PAGE_SIZE,
} from "../utils/hubPagination";

const ALL_STATUSES = Object.values(EntityStatus);
const DEBOUNCE_MS = 300;

export interface BookmarkableHubStateOptions {
  extraParamKeys?: ("version" | "approval" | "pageSize")[];
  /** When set, status URL param uses these values instead of EntityStatus only */
  statusOptions?: string[];
}

export interface BookmarkableHubState {
  q: string;
  setQ: (value: string) => void;
  debouncedQ: string;
  statuses: string[];
  toggleStatus: (status: string) => void;
  page: number;
  setPage: (page: number) => void;
  editUuid: string | null;
  setEditUuid: (uuid: string | null) => void;
  version: string;
  setVersion: (value: string) => void;
  approval: boolean | null;
  setApproval: (value: boolean | null) => void;
  clearAll: () => void;
  searchString: string;
  pageSize: HubPageSize;
  setPageSize: (size: HubPageSize) => void;
}

function parseStatuses(raw: string | null, allowed: string[]): string[] {
  if (!raw) return [];
  return raw
    .split(",")
    .map((s) => s.trim())
    .filter((s) => allowed.includes(s));
}

export function useBookmarkableHubState(
  options: BookmarkableHubStateOptions = {},
): BookmarkableHubState {
  const { extraParamKeys = [], statusOptions } = options;
  const allowedStatuses = statusOptions ?? ALL_STATUSES;
  const [searchParams, setSearchParams] = useSearchParams();

  const qFromUrl = searchParams.get("q") ?? "";
  const [qInput, setQInput] = useState(qFromUrl);
  const [debouncedQ, setDebouncedQ] = useState(qFromUrl);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setQInput(qFromUrl);
    setDebouncedQ(qFromUrl);
  }, [qFromUrl]);

  const statuses = useMemo(
    () => parseStatuses(searchParams.get("status"), allowedStatuses),
    [searchParams, allowedStatuses],
  );

  const page = Math.max(1, parseInt(searchParams.get("page") ?? "1", 10) || 1);
  const editUuid = searchParams.get("edit");
  const version = searchParams.get("version") ?? "";
  const approvalRaw = searchParams.get("approval");
  const approval =
    approvalRaw === "true" ? true : approvalRaw === "false" ? false : null;

  const pageSizeEnabled = extraParamKeys.includes("pageSize");
  const pageSize = pageSizeEnabled
    ? parseHubPageSize(searchParams.get("pageSize"))
    : DEFAULT_HUB_PAGE_SIZE;

  const updateParams = useCallback(
    (mutate: (params: URLSearchParams) => void) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          mutate(next);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const setQ = useCallback(
    (value: string) => {
      setQInput(value);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        updateParams((params) => {
          if (value.trim()) {
            params.set("q", value.trim());
          } else {
            params.delete("q");
          }
          params.set("page", "1");
        });
        setDebouncedQ(value.trim());
      }, DEBOUNCE_MS);
    },
    [updateParams],
  );

  const toggleStatus = useCallback(
    (status: string) => {
      updateParams((params) => {
        const current = parseStatuses(params.get("status"), allowedStatuses);
        const next = current.includes(status)
          ? current.filter((s) => s !== status)
          : [...current, status];
        if (next.length) {
          params.set("status", next.join(","));
        } else {
          params.delete("status");
        }
        params.set("page", "1");
      });
    },
    [updateParams, allowedStatuses],
  );

  const setPage = useCallback(
    (p: number) => {
      updateParams((params) => {
        if (p <= 1) {
          params.delete("page");
        } else {
          params.set("page", String(p));
        }
      });
    },
    [updateParams],
  );

  const setEditUuid = useCallback(
    (uuid: string | null) => {
      updateParams((params) => {
        if (uuid) {
          params.set("edit", uuid);
        } else {
          params.delete("edit");
        }
      });
    },
    [updateParams],
  );

  const setVersion = useCallback(
    (value: string) => {
      if (!extraParamKeys.includes("version")) return;
      updateParams((params) => {
        if (value.trim()) {
          params.set("version", value.trim());
        } else {
          params.delete("version");
        }
        params.set("page", "1");
      });
    },
    [extraParamKeys, updateParams],
  );

  const setApproval = useCallback(
    (value: boolean | null) => {
      if (!extraParamKeys.includes("approval")) return;
      updateParams((params) => {
        if (value === true) {
          params.set("approval", "true");
        } else if (value === false) {
          params.set("approval", "false");
        } else {
          params.delete("approval");
        }
        params.set("page", "1");
      });
    },
    [extraParamKeys, updateParams],
  );

  const setPageSize = useCallback(
    (size: HubPageSize) => {
      if (!pageSizeEnabled) return;
      updateParams((params) => {
        if (size === DEFAULT_HUB_PAGE_SIZE) {
          params.delete("pageSize");
        } else {
          params.set("pageSize", hubPageSizeToParam(size));
        }
        params.set("page", "1");
      });
    },
    [pageSizeEnabled, updateParams],
  );

  const clearAll = useCallback(() => {
    setSearchParams({}, { replace: true });
    setQInput("");
    setDebouncedQ("");
  }, [setSearchParams]);

  const searchString = searchParams.toString()
    ? `?${searchParams.toString()}`
    : "";

  return {
    q: qInput,
    setQ,
    debouncedQ,
    statuses,
    toggleStatus,
    page,
    setPage,
    editUuid,
    setEditUuid,
    version,
    setVersion,
    approval,
    setApproval,
    clearAll,
    searchString,
    pageSize,
    setPageSize,
  };
}

export function filterByHubState<T extends { name: string; status: string }>(
  items: T[],
  state: BookmarkableHubState,
  extraFilter?: (item: T) => boolean,
  searchFields?: (item: T) => string[],
): T[] {
  let result = items;

  if (state.debouncedQ) {
    const query = state.debouncedQ.toLowerCase();
    result = result.filter((item) => {
      const fields = searchFields
        ? searchFields(item)
        : [item.name];
      return fields.some((f) => (f ?? "").toLowerCase().includes(query));
    });
  }

  if (state.statuses.length) {
    result = result.filter((item) => state.statuses.includes(item.status));
  }

  if (extraFilter) {
    result = result.filter(extraFilter);
  }

  return result;
}
